import React from "react";

import { Grid } from "@mui/material";
import { Archive, LocalShipping } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { Access as IAccess } from "../../../../../models/access";
import { LocationType, TreesPowerLineType, QuestionBooleanType } from "../../../../../util/enums";
import Checkbox from "../../../../Form/Checkbox";
import { InstructionsList } from "../../AccessInfoForm";
import AccessField from "../AccessField";
import useStyles from "../styles";

export default function AccessInfo({ access }: { access?: IAccess }) {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const getValueFromBoolean = (value?: boolean): string => {
    if (value) return QuestionBooleanType.YES;

    return QuestionBooleanType.NO;
  };

  const getAccessStepValue = (): string => {
    const steps = access?.steps;

    if (steps) {
      if (access?.stepType) return `${steps} (${access.stepType})`;

      return steps;
    }

    return "";
  };

  const getTreesPowerline = (): string => {
    if (access) {
      const { treeOrPowerlineLow, treeOrPowerlineOverhanging } = access;

      if (treeOrPowerlineLow) return TreesPowerLineType.LOW;
      if (treeOrPowerlineOverhanging) return TreesPowerLineType.OVERHANGING;
    }

    return "";
  };

  return (
    <div className={classes.accessInfoWrapper}>
      <Grid container direction="row" spacing={2}>
        {access?.entryHouseObstructed && (
          <AccessField
            label={translate("willTheEntryOfTheHouseBeObstructed")}
            value={getValueFromBoolean(access?.entryHouseObstructed ?? false)}
            gridSize={12}
          />
        )}
        {access?.driveway && (
          <Grid item container lg={12}>
            <AccessField
              label={translate("driveway")}
              value={access?.driveway.name ?? ""}
              gridSize={4}
            />
            <AccessField
              label={translate("whatIsTheLengthM")}
              value={access?.driveway.length ?? ""}
              gridSize={4}
            />
          </Grid>
        )}
        {access?.residence && (
          <Grid item container lg={12}>
            <AccessField
              label={translate("residenceType")}
              value={access?.residence.type ?? ""}
              gridSize={4}
            />
            {access?.residence?.type === "apartment" && (
              <AccessField
                label={translate("howManyLevels")}
                value={access?.residence.level ?? ""}
                gridSize={4}
              />
            )}
          </Grid>
        )}
        <Grid item container lg={12}>
          <AccessField label={translate("steps")} value={getAccessStepValue()} gridSize={4} />
          <AccessField
            label={translate("hasLift")}
            value={getValueFromBoolean(access?.lift ?? false)}
            gridSize={4}
          />
        </Grid>
        {access?.streetsAccesses && (
          <AccessField
            label={translate("streetAccess")}
            value={access?.streetsAccesses.map((access) => access.name).join(", ") ?? ""}
            gridSize={12}
          />
        )}
        {access?.parking && (
          <Grid item lg={12}>
            <p className={classes.accessInfoLabel}>{translate("parking")}</p>
            <Grid item container lg={6}>
              <AccessField
                label={translate("parkingType")}
                value={access?.parking ?? ""}
                gridSize={6}
                labelClass={classes.parkingTypeLabel}
                valueClass={classes.parkingTypeValue}
              />
              <AccessField
                label={translate("carry")}
                value={access?.carryDistance ?? ""}
                gridSize={6}
                labelClass={classes.parkingTypeLabel}
                valueClass={classes.parkingTypeValue}
              />
            </Grid>
          </Grid>
        )}
        <Grid item container lg={12}>
          <AccessField
            label={translate("treesPowerline")}
            value={getTreesPowerline()}
            gridSize={4}
          />
          <AccessField
            label={translate("action")}
            value={access?.treeOrPowerlineAction ?? ""}
            gridSize={4}
          />
        </Grid>
        <Grid item container lg={12}>
          <AccessField
            label={translate("vehicleSize")}
            value={access?.vehicle ?? ""}
            gridSize={4}
          />
          <AccessField
            label={translate("isFerryRequired")}
            value={getValueFromBoolean(access?.ferryRequired ?? false)}
            gridSize={4}
          />
        </Grid>
        {access?.comments && (
          <AccessField label={translate("comments")} value={access?.comments ?? ""} gridSize={12} />
        )}

        <Grid item lg={12}>
          <Checkbox
            disabled
            checked={access?.accessDetailsDescribed || false}
            label={translate("accessDetailsDescribedAboveAreAccurateToTheBestOfMyKnowledge")}
          />
        </Grid>
        {access?.instructions && access?.instructions?.length > 0 && (
          <Grid item lg={12}>
            <label className={classes.accessInfoLabel}>{translate("specialInstructions")}</label>
            {access &&
              access?.instructions &&
              access?.instructions?.filter(
                (instruction) =>
                  instruction?.when?.toLowerCase() === LocationType.ORIGIN.toLowerCase()
              )?.length > 0 && (
                <InstructionsList
                  isOnViewMode
                  label={translate("pickUp")}
                  icon={<Archive />}
                  data={access?.instructions?.filter(
                    (instruction) =>
                      instruction.when.toLowerCase() === LocationType.ORIGIN.toLowerCase()
                  )}
                />
              )}
            {access &&
              access?.instructions &&
              access?.instructions?.filter(
                (instruction) =>
                  instruction?.when?.toLowerCase() === LocationType.DESTINATION.toLowerCase()
              )?.length > 0 && (
                <InstructionsList
                  isOnViewMode
                  label={translate("delivery")}
                  icon={<LocalShipping />}
                  data={access?.instructions?.filter(
                    (instruction) =>
                      instruction.when.toLowerCase() === LocationType.DESTINATION.toLowerCase()
                  )}
                />
              )}
          </Grid>
        )}
      </Grid>
    </div>
  );
}
